/****************************************************************************
 **
 ** Copyright (C) 2021 The Qt Company Ltd.
 ** Contact: https://www.qt.io/licensing/
 **
 ** This file is part of Qt Bridge Plugin.
 **
 ** Commercial License Usage
 ** Licensees holding valid commercial Qt licenses may use this file in
 ** accordance with the commercial license agreement provided with the
 ** Software or, alternatively, in accordance with the terms contained in
 ** a written agreement between you and The Qt Company. For licensing terms
 ** and conditions see https://www.qt.io/terms-conditions. For further
 ** information use the contact form at https://www.qt.io/contact-us.
 **
 ****************************************************************************/

import { isWebviewPresent, sendToWebview } from "sketch-module-web-view/remote";
import UI from "sketch/ui";
import sketch from "sketch";
import { Rectangle, Document } from "sketch/dom";
import * as Constants from "./constants.js";

export function ViewApi() {
    if (arguments.length < 1) {
        return;
    }
    var apiName = arguments[0];
    delete arguments[0];
    var args = [];
    for (let i in arguments) {
        args.push(JSON.stringify(arguments[i]));
    }
    let api = `${apiName}(${args.join(", ")})`;
    try {
        if (BUtils.isPanelOpen()) {
            sendToWebview("qtbridge.window.panel", api);
        }
    } catch (e) {
        UI.alert("View API failure", e.toString());
    }
}

export function unitedRect(r1, r2) {
    let x = Math.min(r1.x, r2.x);
    let y = Math.min(r1.y, r2.y);
    let x2 = Math.max(r1.x + r1.width, r2.x + r2.width);
    let y2 = Math.max(r1.y + r1.height, r2.y + r2.height);
    return new Rectangle(x, y, x2 - x, y2 - y);
}

export class BUtils {
    static init() {
        let manifestPath = context.plugin.url().path() + "/Contents/Sketch/manifest.json";
        try {
            BUtils.manifest = NSJSONSerialization.JSONObjectWithData_options_error(
                NSData.dataWithContentsOfFile(manifestPath),
                0,
                null
            );
        } catch (ex) {
            BUtils.logError(`Manifest parsing failed. ${ex.toString()}`);
        }
    }

    static appVersion() {
        return sketch.version.sketch + "";
    }

    static metadataVersion() {
        return BUtils.manifest.metadataVersion + "";
    }

    static version() {
        return BUtils.manifest.version + "";
    }

    static build() {
        return BUtils.manifest.build + "";
    }

    static helpLink() {
        return BUtils.manifest.homepage;
    }

    static isPanelOpen() {
        return isWebviewPresent("qtbridge.window.panel");
    }

    static doAction(actionId) {
        let success = false;
        try {
            let currentDocument = Document.getSelectedDocument();
            let action = currentDocument.sketchObject.actionsController().actionForID(actionId);
            if (action) {
                action.doPerformAction(nil);
                success = true;
            }
        } catch (e) {
            BUtils.logExportError(`${e}`)
            success = false;
        }
        return success;
    }

    static showAssertion(title, message, yesMsg = "Yes", noMsg = "No") {
        var alert = NSAlert.alloc().init();
        alert.addButtonWithTitle(yesMsg);
        alert.addButtonWithTitle(noMsg);
        alert.setMessageText(title);
        alert.setInformativeText(message);
        return alert.runModal() == NSAlertFirstButtonReturn;
    }

    static showMessage(title, message) {
        var alert = NSAlert.alloc().init();
        alert.addButtonWithTitle("OK");
        alert.setMessageText(title);
        alert.setInformativeText(message);
        alert.runModal();
    }

    static fileExists(path, isDir = false) {
        const fileManager = NSFileManager.defaultManager();
        let isDirPtr = MOPointer.alloc().initWithValue_(isDir);
        return fileManager.fileExistsAtPath_isDirectory(path, isDirPtr);
    }

    static resourcePath(relPath) {
        return `${context.plugin.urlForResourceNamed("").path() + ""}${relPath}`;
    }

    static getDirPath(currentDir = null) {
        let panel = NSOpenPanel.openPanel();
        panel.setTitle("Choose a location...");
        panel.setPrompt("Ok");
        panel.setCanChooseDirectories(true);
        panel.setShowsHiddenFiles(false);
        panel.setCanChooseFiles(false);
        panel.setAllowsMultipleSelection(false);

        if (currentDir && fileExists(currentDir)) {
            let dirUrl = NSURL.fileURLWithPath(currentDir, true);
            panel.setDirectoryURL(dirUrl);
        }
        const buttonPressed = panel.runModal();
        const newURL = panel.URL();
        panel.close();
        panel = null;
        if (buttonPressed == NSFileHandlingPanelOKButton) {
            return newURL.path() + "";
        }
        return null;
    }

    static makePath(p) {
        if (BUtils.fileExists(p)) {
            return true;
        }
        const fm = NSFileManager.defaultManager();
        let e = MOPointer.alloc().init();
        let r = false;
        r = fm.createDirectoryAtPath_withIntermediateDirectories_attributes_error(p, true, null, e);
        if (!r) {
            BUtils.logError(e.value().localizedDescription());
            return false;
        }
        return true;
    }

    static writeToFile(path, strData) {
        const nsStr = NSString.stringWithFormat("%@", strData);
        let error = MOPointer.alloc().init();
        if (!nsStr.writeToFile_atomically_encoding_error(path, true, NSUTF8StringEncoding, null)) {
            BUtils.logError(error.value() ? error.value().localizedDescription() : "Unknown Error");
            return false;
        }
        return true;
    }

    static removeFile(path) {
        const fileManager = NSFileManager.defaultManager();
        let error = MOPointer.alloc().init();
        if (fileExists(path)) {
            if (!fileManager.removeItemAtPath_error(path, error)) {
                BUtils.logError(error.value().localizedDescription());
            } else {
                return true;
            }
        }
        return false;
    }

    static strToFileName(str) {
        return str.replace(/ /g, "_");
    }

    static logInfo() {
        console.info(...arguments);
        var args = Array.prototype.slice.call(arguments);
        ViewApi("onDebugLog", Constants.LogLevels.Info, args);
    }

    static logWarning() {
        console.warn(...arguments);
        var args = Array.prototype.slice.call(arguments);
        ViewApi("onDebugLog", Constants.LogLevels.Warning, args);
    }

    static logError() {
        console.error(...arguments);
        var args = Array.prototype.slice.call(arguments);
        ViewApi("onDebugLog", Constants.LogLevels.Error, args);
    }

    static updateExportProgress(value) {
        ViewApi("onExportProgress", Number.parseFloat(value / 100).toFixed(2));
    }

    static logExportError() {
        var args = Array.prototype.slice.call(arguments);
        ViewApi("onExportLog", Constants.LogLevels.Error, args.join(", "));
    }

    static logExportWarning() {
        var args = Array.prototype.slice.call(arguments);
        ViewApi("onExportLog", Constants.LogLevels.Warning, args.join(", "));
    }

    static logExportInfo() {
        var args = Array.prototype.slice.call(arguments);
        ViewApi("onExportLog", Constants.LogLevels.Info, args.join(", "));
    }
}
